import React, { useState } from 'react'
import { RouteComponentProps } from 'wouter'
import * as UI from '@mantine/core'
import QRCode from 'react-qr-code'

import { buildComponent } from '../../../components/factory'
import { graphql } from '../../../services/graphql'
import { useOnMount } from '../../../hooks/use-on-mount'

// « Mon compte » : état du second facteur et reconfiguration (#7512).
//
// La reconfiguration se fait depuis une session complète — donc après un code
// déjà validé. C'est ce qui la distingue de l'enrôlement du login : l'API
// refuse un ré-enrôlement adossé au seul mot de passe.

const CODE_LENGTH = 6

const formatDate = (value: string | null) => {
  if (!value) {
    return ''
  }

  const date = new Date(value)

  return Number.isNaN(date.valueOf()) ? '' : date.toLocaleDateString('fr-FR')
}

export const ScreenConnectedAccount = buildComponent<RouteComponentProps>()
  .withLocale('screens.connected.account.screen')
  .withLifecycle(() => {
    const [account, setAccount] = useState<{
      email: string
      enabled: boolean
      verifiedAt: string | null
    } | null>(null)

    const [opened, setOpened] = useState(false)
    const [enrollment, setEnrollment] = useState<{
      otpauthUrl: string
      secret: string
    } | null>(null)
    const [code, setCode] = useState('')
    const [copied, setCopied] = useState(false)
    const [error, setError] = useState<
      '' | 'invalid_code' | 'locked' | 'error'
    >('')
    const [submitting, setSubmitting] = useState(false)
    const [done, setDone] = useState(false)

    const load = () =>
      graphql.queries.getAdministratorInfo().then(({ administrator }) => {
        setAccount({
          email: administrator.email,
          enabled: !!(administrator.totp && administrator.totp.enabled),
          verifiedAt:
            (administrator.totp && administrator.totp.verifiedAt) || null,
        })
      })

    useOnMount(() => {
      load().catch(console.error)
    })

    const close = () => {
      setOpened(false)
      setEnrollment(null)
      setCode('')
      setError('')
      setCopied(false)
      setSubmitting(false)
    }

    const open = () => {
      setDone(false)
      setError('')
      setSubmitting(true)
      setOpened(true)

      // Pas de défi : c'est la session courante qui autorise le geste.
      graphql.mutations
        .totpEnroll()
        .then(({ administratorTotpEnroll }) => {
          if (
            administratorTotpEnroll.error ||
            !administratorTotpEnroll.otpauthUrl ||
            !administratorTotpEnroll.secret
          ) {
            setError('error')
            setSubmitting(false)
            return
          }

          setEnrollment({
            otpauthUrl: administratorTotpEnroll.otpauthUrl,
            secret: administratorTotpEnroll.secret,
          })
          setSubmitting(false)
        })
        .catch((err) => {
          console.error(err)
          setError('error')
          setSubmitting(false)
        })
    }

    const confirm = (event: React.FormEvent) => {
      event.preventDefault()

      if (!/^[0-9]{6}$/.test(code)) {
        setError('invalid_code')
        return
      }

      setError('')
      setSubmitting(true)

      graphql.mutations
        .totpVerify(code)
        .then(({ administratorTotpVerify }) => {
          if (administratorTotpVerify.error) {
            setError(
              administratorTotpVerify.error === 'locked'
                ? 'locked'
                : 'invalid_code',
            )
            setCode('')
            setSubmitting(false)
            return
          }

          setDone(true)
          close()
          return load()
        })
        .catch((err) => {
          console.error(err)
          setError('error')
          setSubmitting(false)
        })
    }

    return {
      account,
      opened,
      open,
      close,
      enrollment,
      code,
      setCode,
      copied,
      copySecret: () => {
        if (!enrollment) {
          return
        }
        navigator.clipboard?.writeText(enrollment.secret).catch(() => {})
        setCopied(true)
      },
      error,
      submitting,
      done,
      confirm,
    }
  })
  .withRender(({ lifecycle, locale }) => (
    <>
      <UI.Stack
        sx={{
          position: 'sticky',
          top: 72,
          zIndex: 10,
          backgroundColor: '#f8f9fc',
        }}
        mx={-16}
        px={16}
        mt={-16}
        py={16}
        spacing={0}
      >
        <UI.Title>
          <UI.Text size={22}>{locale('title')}</UI.Text>
        </UI.Title>
        <UI.Text color="dimmed">{lifecycle.account?.email ?? '...'}</UI.Text>
      </UI.Stack>

      <UI.Stack sx={{ maxWidth: 1280, margin: '2rem auto 32rem' }} spacing={32}>
        <UI.Card shadow="sm" pb={32} px={24}>
          <UI.Input.Wrapper
            label={<b style={{ fontSize: '2rem' }}>{locale('security')}</b>}
            description={
              <UI.Text mt={8} size="sm" sx={{ maxWidth: '124rem' }}>
                {locale('description')}
              </UI.Text>
            }
          >
            <UI.Group mt={24} spacing={16}>
              <UI.Badge
                size="lg"
                color={lifecycle.account?.enabled ? 'green' : 'gray'}
              >
                {locale('twoFactor')} :{' '}
                {locale(lifecycle.account?.enabled ? 'enabled' : 'disabled')}
              </UI.Badge>

              {lifecycle.account?.verifiedAt && (
                <UI.Text size="sm" color="dimmed">
                  {locale('verifiedAt', {
                    date: formatDate(lifecycle.account.verifiedAt),
                  })}
                </UI.Text>
              )}
            </UI.Group>

            {lifecycle.done && (
              <UI.Alert mt={16} color="green">
                {locale('done')}
              </UI.Alert>
            )}

            <UI.Text size="sm" color="dimmed" mt={16}>
              {locale('reconfigureHelp')}
            </UI.Text>

            <UI.Button mt={16} variant="light" onClick={lifecycle.open}>
              {locale('reconfigure')}
            </UI.Button>
          </UI.Input.Wrapper>
        </UI.Card>
      </UI.Stack>

      <UI.Modal
        opened={lifecycle.opened}
        onClose={lifecycle.close}
        title={<UI.Text weight="bold">{locale('reconfigure')}</UI.Text>}
        zIndex={1000}
        padding="lg"
      >
        <form onSubmit={lifecycle.confirm}>
          {lifecycle.enrollment ? (
            <>
              <UI.Text size="sm">{locale('scan')}</UI.Text>

              <UI.Box
                mt={16}
                sx={{
                  background: 'white',
                  padding: 16,
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <QRCode value={lifecycle.enrollment.otpauthUrl} size={180} />
              </UI.Box>

              <UI.Text size="sm" color="dimmed" mt={16}>
                {locale('manual')}
              </UI.Text>
              <UI.Group spacing={8} mt={8}>
                <UI.Code sx={{ fontSize: '1.4rem' }}>
                  {lifecycle.enrollment.secret}
                </UI.Code>
                <UI.Button
                  size="xs"
                  variant="light"
                  type="button"
                  onClick={lifecycle.copySecret}
                >
                  {locale(lifecycle.copied ? 'copied' : 'copy')}
                </UI.Button>
              </UI.Group>

              <UI.TextInput
                autoFocus
                mt="md"
                disabled={lifecycle.submitting}
                value={lifecycle.code}
                onChange={(event) =>
                  lifecycle.setCode(
                    event.currentTarget.value
                      .replace(/[^0-9]/g, '')
                      .slice(0, CODE_LENGTH),
                  )
                }
                label={locale('code')}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
              />
            </>
          ) : (
            <UI.Center py={32}>
              <UI.Loader />
            </UI.Center>
          )}

          {lifecycle.error && (
            <UI.Alert mt="md" color="red">
              <UI.Text color="red">{locale(lifecycle.error)}</UI.Text>
            </UI.Alert>
          )}

          <UI.Group mt="xl" position="right">
            <UI.Button
              variant="default"
              type="button"
              onClick={lifecycle.close}
            >
              {locale('cancel')}
            </UI.Button>
            <UI.Button
              type="submit"
              disabled={
                lifecycle.submitting ||
                !lifecycle.enrollment ||
                lifecycle.code.length < CODE_LENGTH
              }
            >
              {locale('confirm')}
            </UI.Button>
          </UI.Group>
        </form>
      </UI.Modal>
    </>
  ))
